// tslint:disable: no-console
import { getServices } from "../../api/getServices";
import { setBatchedRunListener } from "../redux/actions/firebase";
import { updateRunStatus } from "../redux/actions/runs";
import {
  fetchVersionsFailure,
  fetchVersionsStarted,
  fetchVersionsSuccess,
} from "../redux/actions/versions";
import { getCurrentKernelRunId } from "../redux/selectors/current";
import { getStore } from "../redux/store/appStore";
import { Accelerator } from "../types/Accelerator";
import { Run, stringToRunStatus } from "../types/entities/run";
import { Version } from "../types/entities/version";

export async function getKernelVersions() {
  const services = getServices();
  const state = getStore().getState();
  const id = state.notebook.kernel;

  if (!id) {
    return;
  }

  try {
    fetchVersionsStarted();
    const response = await services.kernelsClient.listKernelVersions({
      kernelId: id,
    });

    const versions: Version[] = [];
    const runs: Run[] = [];
    for (const item of response.items) {
      const { author, blob, run, version } = item;

      if (!author || !blob || !run || !version) {
        fetchVersionsFailure(id, "Invalid response.");
        return;
      }

      const kernelId = id;
      const title = run.title || "";
      const versionId = version.id;
      const status = run.status;
      const linesInserted = version.linesInserted;
      const linesRemoved = version.linesDeleted;
      const linesTotal = version.linesTotal;
      const versionNumber = version.versionNumber;
      const runId = run.id;
      const authorId = author.id;
      const displayName = author.displayName as string;
      const thumbnailUrl = author.thumbnailUrl as string;
      const blobId = blob.id;
      const dateCreated = blob.dateCreated;
      // scriptLanguageName is the human-readable version of the language name, which is optional
      // language is used as a fallback in that case
      const language = run.scriptLanguageName || run.language;
      const outputSizeBytes = run.runInfo ? run.runInfo.outputSizeBytes : 0;
      const runTimeSeconds = run.runInfo ? run.runInfo.runTimeSeconds : 0;
      const exitCode = run.runInfo ? run.runInfo.exitCode : undefined;
      const succeeded = run.runInfo ? run.runInfo.succeeded : false;
      const renderedOutputUrl = run.renderedOutputUrl;
      const versionType = version.versionType;

      if (
        title === undefined ||
        versionId === undefined ||
        status === undefined ||
        linesInserted === undefined ||
        linesRemoved === undefined ||
        linesTotal === undefined ||
        runId === undefined ||
        authorId === undefined ||
        authorId === null ||
        displayName === undefined ||
        blobId === undefined ||
        thumbnailUrl === undefined ||
        dateCreated === undefined ||
        language === undefined
      ) {
        fetchVersionsFailure(id, "Invalid entities in response.");
        return;
      }

      versions.push({
        id: versionId,
        kernel: kernelId,
        title,
        status,
        isNotebook: run.sourceType === "notebook",
        language,
        exitCode,
        succeeded,
        linesInserted,
        linesRemoved,
        linesTotal,
        pinningType: version.pinningType,
        versionNumber,
        outputSizeBytes,
        runTimeSeconds,
        renderedOutputUrl,
        versionName: version.versionName,
        versionType,
        viewerUrl: item.viewerUrl,

        author: {
          id: authorId,
          displayName,
          thumbnailUrl,
        },

        blob: {
          id: blobId,
          dateCreated: new Date(dateCreated),
        },

        runId,
      });

      runs.push({
        id: runId,
        status: stringToRunStatus(status),
        exitCode,
        resourceUsage: null,
        logs: null,
        versionId,
        stdOut: [],
        stdErr: [],
        datasetDownloadProgressMessages: [],
        dateCreated: run.dateCreated || null,
      });
    }

    updateRunStatus(runs);
    fetchVersionsSuccess(id, versions);

    for (const version of versions) {
      if (version.status === "running") {
        watchRunForStatus(
          version.runId,
          state.notebook.sequence,
          version.versionType,
          "none",
        );
      }
    }
  } catch (error) {
    fetchVersionsFailure(id, error);
  }
}

const watchedRunIds: Set<number> = new Set();

export const watchRunForStatus = async (
  kernelRunId: number,
  sequence: number | null,
  versionType: "batch" | "interactive" | "quick",
  accelerator: Accelerator,
) => {
  const state = getStore().getState();

  /** Used in debugging and taking different actions on failure if this is the current interactive kernel run. */
  const isInteractive = getCurrentKernelRunId(state) === kernelRunId;

  if (isInteractive || watchedRunIds.has(kernelRunId)) {
    return;
  }
  // We know it's only "batch" or "quick" here, but the compiler doesn't.
  const type = versionType === "quick" ? "quick" : "batch";
  await setBatchedRunListener(kernelRunId, sequence || 0, type, accelerator);
  watchedRunIds.add(kernelRunId);
};
